import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { loadHyperVergeSDK } from "../../lib/hyperverge";
import AfterOnboardAPI from "../../services/AfterOnboardAPI";
import style from "./VideoKyc.module.scss";
import { setVKYCStep } from "../../redux/slices/userSlice";
import moment from "moment";
import { clevertap__VKYC_session_launched } from "../../lib/clevertap-events";
import { handlehideContextualLoader } from "../../utils/contextualLoaderTrigger";

export default function VideoKyc() {
  const [error, setError] = useState(null);

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const hasStartedKyc = useRef(false);

  useEffect(() => {
    if (hasStartedKyc.current) return;

    hasStartedKyc.current = true;

    handleKyc();
  }, []);

  const handleKyc = async () => {
    dispatch(setVKYCStep("VKYC_INITIATE"));

    const { vkycInitiateAPI } = new AfterOnboardAPI();

    try {
      // 1. Load HyperVerge SDK
      await loadHyperVergeSDK();

      // 2. Get data from backend
      const { data } = await vkycInitiateAPI();

      const { authToken, workflow_id, transaction_id } = data;

      // 3. 72-hour KYC validity check
      const kycDate = moment(data?.aadharKycDate, "YYYYMMDDHHmmssSSS", true);

      if (kycDate.isValid() && moment().diff(kycDate, "hours", true) > 72) {
        navigate("/aadhaar-verification");
        return;
      }

      // 4. Create HyperVerge configuration
      const hyperKycConfig = new window.HyperKycConfig(authToken, true);

      // 5. Optional configurations
      hyperKycConfig.setInputs({
        panNumber: data.panNumber,
        aadhaarName: data.aadharFullName,
        aadhaarImage: data.profileImage,
        aadhaarCreatedDate: data.aadharKycDate,
        currentAddress: data.currentAddress,
        panName: data.panName,
      });

      hyperKycConfig.setUseLocation(true);

      hyperKycConfig.setDefaultLangCode("en");

      hyperKycConfig.setUniqueId(transaction_id);

      // 6. Launch SDK
      await window.HyperKYCModule.launch(hyperKycConfig, handleKycResult);

      await clevertap__VKYC_session_launched(transaction_id);
    } catch (error) {
      console.error("HyperVerge error:", error);
      setError(error?.response?.data?.message);

      if (error?.response?.data?.data?.redirectTo === "AADHAR") {
        // navigate("/enter-aadhar");
        localStorage.setItem("aadhaar_vkyc_repeat", "true");
        navigate("/select-loan");
        return;
      }
    } finally {
      handlehideContextualLoader();
    }
  };

  const handleKycResult = (result) => {
    console.log("HyperVerge result:", result);

    localStorage.setItem("transaction_id", result?.transactionId);
    localStorage.setItem("vkyc_status", result?.status);

    switch (result.status) {
      case "user_cancelled":
        navigate("/video-kyc-retry", {
          state: {
            transaction_id: result?.transactionId || "",
            vkyc_status: result?.status || "",
            api_response: result,
          },
        });
        break;

      case "error":
        navigate("/video-kyc-retry", {
          state: {
            transaction_id: result?.transactionId || "",
            vkyc_status: result?.status || "",
            api_response: result,
          },
        });
        break;

      case "auto_declined":
        navigate("/video-kyc-retry", {
          state: {
            transaction_id: result?.transactionId || "",
            vkyc_status: result?.status || "",
            api_response: result,
          },
        });
        break;

      case "needs_review":
        navigate("/video-kyc-review", {
          state: {
            transaction_id: result?.transactionId || "",
            vkyc_status: result?.status || "",
            api_response: result,
          },
        });
        break;

      case "manually_approved":
        navigate("/video-kyc-approved", {
          state: {
            transaction_id: result?.transactionId || "",
            vkyc_status: result?.status || "",
            api_response: result,
          },
        });
        break;

      case "manually_rejected":
      case "manually_declined":
        navigate("/video-kyc-rejected", {
          state: {
            transaction_id: result?.transactionId || "",
            vkyc_status: result?.status || "",
            api_response: result,
          },
        });
        break;

      default:
        console.warn("Unknown status:", result.status);
    }
  };

  if (error) {
    return (
      <div className={style.errorMessage}>
        <p>{error}</p>
      </div>
    );
  }
  return <div></div>;
}
